import { Inject, Injectable } from '@angular/core';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import { Pipeline } from '@vendasta/sales-common';
import {
  DurationInterface,
  ListOpportunitiesRequestSortField as SortField,
  ListOpportunitiesRequestSortOptionInterface as SortOption,
  Opportunity,
  SalesOpportunitiesApiService,
  SalesOpportunitiesSdk,
  SalesOpportunitiesService,
  SortDirection,
} from '@vendasta/sales-opportunities';
import { BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject, throwError } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { PipelineStats } from '../pipelines/pipeline-stats/pipeline-stats.service';
import { OpportunitiesCommands, UpdateRequest, UpdateRequestType } from './interface';
import { PIPELINES_TOKEN, SALES_PERSON_ID_TOKEN } from './tokens';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpportunitySidePanelEventsService } from './opportunity-side-panel/opportunity-side-panel-events.service';

/**
 * Provides a RXJS+CQRS model for all of the opportunities updated on the
 * client-side.
 */
@Injectable({ providedIn: 'root' })
export class OpportunityService implements OpportunitiesCommands {
  logErrors = true;
  private readonly workStateMap = new ObservableWorkStateMap<string, Opportunity>(this.logErrors);
  public readonly state: ObservableWorkStateMap<string, Opportunity> = this.workStateMap;
  private readonly updateRequests$$ = new ReplaySubject<UpdateRequest>(1);
  private readonly updateRollbacks$$ = new ReplaySubject<Opportunity>(1);
  private readonly changes$: Observable<Opportunity[]>;

  private readonly currentOpportunity$$ = new BehaviorSubject<Opportunity>(null);
  public readonly currentOpportunity$ = this.currentOpportunity$$.pipe(filter<Opportunity>(Boolean));

  constructor(
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
    @Inject(SALES_PERSON_ID_TOKEN) readonly salesPersonId$: Observable<string>,
    @Inject(PIPELINES_TOKEN) readonly pipelines$: Observable<Pipeline[]>,
    private readonly pipelineRevenue: PipelineStats,
    private readonly salesOpportunitiesApiService: SalesOpportunitiesApiService,
    private readonly snackbarService: SnackbarService,
    private readonly opportunitySidePanelEventService: OpportunitySidePanelEventsService,
  ) {
    this.updateRequests$$
      .pipe(
        withLatestFrom(this.salesPersonId$),
        tap(([req, spId]) => this.handleRequest(req, spId)),
      )
      .subscribe();
    this.changes$ = buildListFromEvents(merge(this.updateRequests$$, this.updateRollbacks$$), (u) => u.opportunityId);
  }

  isEditable$(): Observable<boolean> {
    return this.currentOpportunity$.pipe(map((o) => (o ? !o.isClosed : false)));
  }

  hasSalespersonAccess$(): Observable<boolean> {
    return this.salesPersonId$.pipe(map(Boolean));
  }

  public addAttachmentToOpportunity(
    opportunityId: string,
    accountGroupId: string,
    name: string,
    url: string,
  ): Observable<Opportunity> {
    return this.opportunityApi.addAttachment(opportunityId, accountGroupId, name, url).pipe(
      take(1),
      switchMap(() => this.loadOpportunityAndSetSelected(opportunityId, accountGroupId)), //reload
    );
  }

  updateOpportunitySalesperson(
    opportunity: Opportunity,
    salespersonId: string,
    editSalespersonId: string,
    createSalesActivity?: boolean,
  ): Observable<Opportunity> {
    return this.opportunityApi
      .updateOpportunitySalesperson(
        opportunity.accountGroupId,
        opportunity.opportunityId,
        editSalespersonId,
        salespersonId,
        createSalesActivity,
      )
      .pipe(
        catchError(() => {
          return of(null);
        }),
        withLatestFrom(this.currentOpportunity$),
        map(([newOpp, oldOpp]) => {
          if (!newOpp) {
            this.setSelectedOpportunity(oldOpp);
          } else {
            this.setSelectedOpportunity(newOpp);
          }
          return newOpp;
        }),
      );
  }

  updateProbability(opportunity: Opportunity, newProbability: number): void {
    const request = Object.assign({}, opportunity as UpdateRequest);
    request.requestType = UpdateRequestType.PROBABILITY;
    request.probabilityIfUpdateFails = opportunity.probability;
    request.probability = newProbability;

    this.updateRequests$$.next(request);
  }

  public updateExpectedContractDuration(
    duration: DurationInterface,
    opportunity: Opportunity,
  ): Observable<Opportunity> {
    if (!opportunity || !duration) {
      return of(null);
    }
    return this.opportunityApi
      .updateOpportunityDuration(opportunity.opportunityId, opportunity.accountGroupId, undefined, duration)
      .pipe(
        catchError(() => {
          return of(null);
        }),
        withLatestFrom(this.currentOpportunity$),
        map(([newOpp, oldOpp]) => {
          if (!newOpp) {
            this.setSelectedOpportunity(oldOpp);
          } else {
            this.setSelectedOpportunity(newOpp);
          }
          return newOpp;
        }),
      );
  }

  public updateExpectedCloseDate(opportunity: Opportunity, newDate: Date): Observable<Opportunity> {
    if (!opportunity || !newDate) {
      return of(null);
    }
    return this.opportunityApi
      .updateOpportunityExpectedCloseDate(
        opportunity.accountGroupId,
        opportunity.opportunityId,
        opportunity.salesPersonId,
        newDate,
        true,
      )
      .pipe(
        catchError(() => {
          return of(null);
        }),
        withLatestFrom(this.currentOpportunity$),
        map(([newOpp, oldOpp]) => {
          if (!newOpp) {
            this.setSelectedOpportunity(oldOpp);
          } else {
            this.setSelectedOpportunity(newOpp);
          }
          return newOpp;
        }),
      );
  }

  updatePipelineId(opportunity: Opportunity, newPipelineId: string, newProbability: number): void {
    const request = Object.assign({}, opportunity as UpdateRequest);
    request.requestType = UpdateRequestType.PIPELINE_ID;
    request.pipelineIdIfUpdateFails = opportunity.pipelineId;
    request.probabilityIfUpdateFails = opportunity.probability;
    request.pipelineId = newPipelineId;
    request.probability = newProbability;
    this.updateRequests$$.next(request);
  }

  public updateType(opportunity: Opportunity, newType: string): Observable<Opportunity> {
    if (!opportunity) {
      return of(null);
    }
    return this.opportunityApi
      .updateOpportunityType(opportunity.opportunityId, opportunity.accountGroupId, newType, opportunity.salesPersonId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
        withLatestFrom(this.currentOpportunity$),
        map(([newOpp, oldOpp]) => {
          if (!newOpp) {
            this.setSelectedOpportunity(oldOpp);
          } else {
            this.setSelectedOpportunity(newOpp);
          }
          return newOpp;
        }),
      );
  }

  updateTags(tags: string[]): Observable<Opportunity> {
    return this.currentOpportunity$.pipe(
      take(1),
      switchMap((o) => this.opportunityApi.updateTags(o.opportunityId, o.accountGroupId, o.salesPersonId, tags)),
      catchError(() => of(null)),
      withLatestFrom(this.currentOpportunity$),
      map(([newOpp, oldOpp]) => {
        this.setSelectedOpportunity(newOpp || oldOpp);
        return newOpp;
      }),
    );
  }

  updateSubscribers(subscriberUserIds: string[]): Observable<Opportunity> {
    return this.currentOpportunity$.pipe(
      take(1),
      switchMap((o) =>
        this.opportunityApi
          .updateSubscriberUserIdsForOpportunity(o.opportunityId, o.accountGroupId, o.salesPersonId, subscriberUserIds)
          .pipe(
            catchError((err) => {
              this.setSelectedOpportunity(o);
              throw err;
            }),
            switchMap(() => this.opportunityApi.get(o.opportunityId, o.accountGroupId)),
          ),
      ),
      withLatestFrom(this.currentOpportunity$),
      map(([newOpp, oldOpp]) => {
        this.setSelectedOpportunity(newOpp || oldOpp);
        return newOpp;
      }),
    );
  }

  setSelectedOpportunity(opportunity: Opportunity): void {
    if (opportunity) {
      this.currentOpportunity$$.next(opportunity);
      this.opportunitySidePanelEventService.opportunityUpdated(opportunity);
    } else {
      console.error('cannot set selected opportunity to null');
    }
  }

  loadOpportunityAndSetSelected(opportunityId: string, accountId: string): Observable<Opportunity> {
    const opp$ = this.opportunityApi.get(opportunityId, accountId);
    const result$ = merge(opp$, this.state.getWorkResults$(opportunityId)).pipe(first());
    result$.subscribe((value) => this.currentOpportunity$$.next(value));
    return result$;
  }

  applyForStage$(
    opportunities$: Observable<Opportunity[]>,
    stageStart: number,
    stageEnd: number,
    option?: SortOption,
  ): Observable<Opportunity[]> {
    return combineLatest([opportunities$, this.changes$]).pipe(
      map(([opps, changes]) => {
        const moved = addOrRemoveFromStage(changes, opps, stageStart, stageEnd, option);
        return preferUpdatedOverFetched(changes, moved);
      }),
    );
  }

  public updateNote(newNote: string): Observable<Opportunity> {
    return this.currentOpportunity$.pipe(
      take(1),
      switchMap((o) =>
        this.opportunityApi.updateOpportunityNotes(o.opportunityId, o.accountGroupId, o.salesPersonId, newNote),
      ),
      catchError(() => of(null)),
      withLatestFrom(this.currentOpportunity$),
      map(([newOpp, oldOpp]) => {
        this.setSelectedOpportunity(newOpp || oldOpp);
        return newOpp;
      }),
    );
  }

  public updateName(newName: string): Observable<Opportunity> {
    return this.currentOpportunity$.pipe(
      take(1),
      switchMap((o) =>
        this.salesOpportunitiesApiService.updateOpportunityName({
          opportunityId: o.opportunityId,
          accountGroupId: o.accountGroupId,
          editSalesPersonId: o.salesPersonId,
          name: newName,
        }),
      ),
      catchError(() => of(null)),
      map((updateNameResponse) => updateNameResponse?.opportunity),
      withLatestFrom(this.currentOpportunity$),
      map(([newOpp, oldOpp]) => {
        this.setSelectedOpportunity(newOpp || oldOpp);
        return newOpp;
      }),
    );
  }

  private doUpdateProbability(req: UpdateRequest, salespersonId: string): void {
    this.workStateMap.startWork(
      req.opportunityId,
      this.opportunityApi
        .updateOpportunityProbability(req.opportunityId, req.accountGroupId, salespersonId, req.probability)
        .pipe(
          catchError((err) => {
            this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.SALES_OPPORTUNITIES.ERRORS.UPDATE_OPPORTUNITY');
            const rollback = Object.assign({}, req);
            rollback.probability = req.probabilityIfUpdateFails;
            this.updateRollbacks$$.next(rollback);
            return throwError(err); // Must rethrow to stop WorkStateMap loading.
          }),
          tap((opp) => {
            if (this.currentOpportunity$$.getValue() !== null) {
              this.setSelectedOpportunity(opp);
            }
            this.updateRevenuesForProbabilityUpdate(req);
          }),
        ),
    );
  }

  private doUpdatePipeline(req: UpdateRequest, salespersonId: string): void {
    this.workStateMap.startWork(
      req.opportunityId,
      this.opportunityApi
        .updateOpportunityPipeline(req.opportunityId, req.accountGroupId, salespersonId, req.pipelineId)
        .pipe(
          switchMap(() => {
            return this.opportunityApi.updateOpportunityProbability(
              req.opportunityId,
              req.accountGroupId,
              salespersonId,
              req.probability,
            );
          }),
          tap((opp) => this.currentOpportunity$$.next(opp)),
          catchError((err) => {
            this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.SALES_OPPORTUNITIES.ERRORS.UPDATE_OPPORTUNITY');
            const rollback = Object.assign({}, req);
            rollback.pipelineId = req.pipelineIdIfUpdateFails;
            this.updateRollbacks$$.next(rollback);
            return throwError(err); // Must rethrow to stop WorkStateMap loading.
          }),
          map((opportunity) => {
            this.updateRevenuesForPipelineUpdate(req);
            return opportunity;
          }),
        ),
    );
  }

  private updateRevenuesForProbabilityUpdate(request: UpdateRequest): void {
    this.pipelines$.pipe(take(1)).subscribe((pipelines) => {
      const pipeline = pipelines.find((p) => p.id === request.pipelineId);
      const stageToRemoveFrom = pipeline.findStage(request.probabilityIfUpdateFails);
      const stageToAddToo = pipeline.findStage(request.probability);
      this.pipelineRevenue.transferStatsBetweenStages(
        stageToRemoveFrom.stageId,
        stageToAddToo.stageId,
        request.projectedFirstYearValue,
      );
    });
  }

  private updateRevenuesForPipelineUpdate(request: UpdateRequest): void {
    this.pipelines$.pipe(take(1)).subscribe((pipelines) => {
      const pipelineToRemoveFrom = pipelines.find((p) => p.id === request.pipelineIdIfUpdateFails);
      const pipelineToMoveTo = pipelines.find((p) => p.id === request.pipelineId);

      const stageToRemoveFrom = pipelineToRemoveFrom.findStage(request.probabilityIfUpdateFails);
      const stageToAddToo = pipelineToMoveTo.findStage(request.probability);

      this.pipelineRevenue.transferStatsBetweenStages(
        stageToRemoveFrom.stageId,
        stageToAddToo.stageId,
        request.projectedFirstYearValue,
      );
    });
  }

  private handleRequest(req: UpdateRequest, userId: string): void {
    switch (req.requestType) {
      case UpdateRequestType.PROBABILITY:
        return this.doUpdateProbability(req, userId);
      case UpdateRequestType.PIPELINE_ID:
        return this.doUpdatePipeline(req, userId);
      default:
        throw new Error(`Unexpected request type: ${req.requestType} [${JSON.stringify(req)}]`);
    }
  }
}

export function rollbackProbabilityForFailures(
  opps: Opportunity[],
  oppId: string,
  previousProb: number,
): Opportunity[] {
  return opps.map((opp) => {
    if (opp.opportunityId === oppId) {
      opp.probability = previousProb;
    }
    return opp;
  });
}

export function preferUpdatedOverFetched(updated: Opportunity[], fetched: Opportunity[]): Opportunity[] {
  return fetched.map((fetchedOpp) => {
    const updatedOpp = updated.find((o) => o.opportunityId === fetchedOpp.opportunityId);
    if (!updatedOpp) {
      return fetchedOpp;
    }
    fetchedOpp.probability = updatedOpp.probability;
    return fetchedOpp;
  });
}

export function addOrRemoveFromStage(
  changes: Opportunity[],
  opps: Opportunity[],
  stageStart: number,
  stageEnd: number,
  option?: SortOption,
): Opportunity[] {
  const removedOpps = changes
    .filter((change) => {
      const opp = opps.find((op) => op.opportunityId === change.opportunityId);
      if (!!opp && change.pipelineId !== opp.pipelineId) {
        return true;
      }

      return change.probability > stageEnd || change.probability < stageStart;
    })
    .map((u) => u.opportunityId);

  const oppsWithRemoved = opps.filter((opp) => removedOpps.indexOf(opp.opportunityId) === -1);
  const addedOpps = changes.filter(
    (change) =>
      change.probability <= stageEnd &&
      change.probability >= stageStart &&
      oppsWithRemoved.findIndex((o) => o.opportunityId === change.opportunityId) === -1,
  );

  const opportunities = oppsWithRemoved.concat(addedOpps);
  return sortOpportunitiesByOption(opportunities, option);
}

function sortOpportunitiesByOption(opps: Opportunity[], option?: SortOption): Opportunity[] {
  if (!option) {
    return opps;
  }
  switch (option.field) {
    case SortField.PROJECTED_FIRST_YEAR_VALUE:
      return sortOpportunities(
        opps,
        (o) => (o.projectedFirstYearValue = o.projectedFirstYearValue || 0),
        option.direction,
      );
    case SortField.EXPECTED_CLOSE:
      return sortOpportunities(opps, (o) => o.expectedCloseDate, option.direction);
    case SortField.LAST_CONNECTED:
      return sortOpportunities(opps, (o) => o.lastConnected, option.direction);
    case SortField.LAST_ACTIVITY:
      return sortOpportunities(opps, (o) => o.lastActivityDate, option.direction);
    case SortField.NAME:
      return sortOpportunities(opps, (o) => o.name, option.direction);
    case SortField.CREATED:
      return sortOpportunities(opps, (o) => o.created, option.direction);
    case SortField.MEDDIC:
      return sortOpportunities(opps, (o) => o.scoring, option.direction);
  }
  return opps;
}

function sortOpportunities(
  opps: Opportunity[],
  sortField: (Opportunity) => any,
  direction: SortDirection,
): Opportunity[] {
  const dir = direction === SortDirection.ASCENDING ? 1 : -1;
  return opps.sort((a, b) => compareBySortDirection(sortField(a), sortField(b), dir));
}

function compareBySortDirection(value1: any, value2: any, direction: number): number {
  if (value1 instanceof Date || value2 instanceof Date) {
    value1 = value1 || new Date(0);
    value2 = value2 || new Date(0);
  }

  if (value1 > value2) {
    return direction;
  } else if (value1 < value2) {
    return direction * -1;
  }

  return 0;
}

function buildListFromEvents<EVENT>(events: Observable<EVENT>, idExtractor: (e: EVENT) => string): Observable<EVENT[]> {
  return events.pipe(
    scan((handledSoFar: EVENT[], newUpdate: EVENT) => {
      const i = handledSoFar.findIndex((o) => idExtractor(o) === idExtractor(newUpdate));
      if (i < 0) {
        return handledSoFar.concat([newUpdate]);
      }
      return handledSoFar.map((val, index) => (index === i ? newUpdate : val));
    }, []),
    startWith([]),
    shareReplay(1),
  );
}
